// ---------------------------------------------------------------------------
// YouAreYou (uru) — DashboardPage  (2026 rose refresh)
//
// The authenticated home. Matches screen-mockups/redesign/dashboard_youareyou:
//   - "Welcome back, {name}" hero
//   - Global Trust Link card (the input-driven smart link bar: create/send/QR)
//   - Trust Settings dark-rose card → opens the requirements modal
//   - Recent Activity card (real connections feed)
//   - Elevate your Trust Score card (shown until core KYC is complete)
//   - "How it works" collapsible
//
// All data/wiring is real (no invented content):
//   GET /api/uru/me → global_link, name, KYC scorecard, connection_config
//   GET /api/uru/connections → recent activity
//   POST /api/uru/connections/create → create/send a direct trust link
//   POST /api/uru/me/config → save default requirements
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import step1Img from "@/assets/step2.png";
import step2AnimImg from "@/assets/step2-animation.apng";
import step2StaticImg from "@/assets/step2new.png";
import step3Img from "@/assets/step3.png";
import {
  listConnections,
  getConnection,
  getUruMe,
  saveGlobalLinkConfig,
  createDirectLink,
  normalizePhone,
} from "@/api/endpoints";
import { ApiRequestError, NetworkError } from "@/api/client";
import { resolveAvatarUrl } from "@/types/api";
import type { Connection, CreateDirectLinkConfig } from "@/types/api";
import TrustLinkSettingsModalV2 from "@/components/ui/TrustLinkSettingsModalV2";
import type { TrustLinkSettingsV2, MyFactorData } from "@/components/ui/TrustLinkSettingsModalV2";
import ContactDetailModal from "@/components/ui/ContactDetailModal";
import QrShareModal from "@/components/ui/QrShareModal";
import { realtime } from "@/services/realtime";
import {
  detectRecipientType,
  formatPhoneDisplay,
  copyToClipboard,
  statusColorClass,
  statusLabel,
} from "@/lib/utils";

const CARD = "rounded-2xl bg-white shadow-[0px_4px_20px_rgba(26,31,43,0.04)]";

// ---------------------------------------------------------------------------
// Icon helper — renders Material Symbols Outlined
// ---------------------------------------------------------------------------

function Icon({
  name,
  filled = false,
  className = "",
}: {
  name: string;
  filled?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`material-symbols-outlined select-none ${className}`}
      style={filled ? { fontVariationSettings: "'FILL' 1" } : undefined}
    >
      {name}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Feature cards data ("How it works")
// ---------------------------------------------------------------------------

interface FeatureCard {
  step: number;
  image: string;
  title: string;
  description: string;
}

const FEATURE_CARDS: FeatureCard[] = [
  {
    step: 1,
    image: step1Img,
    title: "Send your Trust Link to Someone Unknown",
    description:
      "Share your link with someone you want verified as 'really them' — by text, email, or QR code.",
  },
  {
    step: 2,
    image: step2StaticImg,
    title: "They provide proof",
    description:
      "The recipient securely submits their ID, phone, and liveness check — only the verifications you require.",
  },
  {
    step: 3,
    image: step3Img,
    title: "Proceed with confidence",
    description:
      "You know they are who they say they are. They know you are too. Move forward with certainty.",
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(unixTimestamp: number): string {
  const now = Date.now() / 1000;
  const diff = now - unixTimestamp;

  if (diff < 60) return "Just now";
  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  }
  if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  }
  if (diff < 172800) return "Yesterday";

  const days = Math.floor(diff / 86400);
  return `${days} day${days !== 1 ? "s" : ""} ago`;
}

/** True if the string looks like a raw phone number rather than a label. */
function isPhoneLikeLabel(s: string): boolean {
  const t = s.trim();
  return t.startsWith("+") || /^\d[\d\s\-().]*$/.test(t);
}

// ---------------------------------------------------------------------------
// SmartLinkBar — input-driven trust link bar (rose refresh)
//   Empty → "Create Trust Link" · Phone/Email → "Send Trust Link" · Label → "Create & Copy"
// ---------------------------------------------------------------------------

interface SmartLinkBarProps {
  recipient: string;
  onRecipientChange: (value: string) => void;
  loading?: boolean;
  acting?: boolean;
  onSettingsClick: () => void;
  onQrClick: (recipient?: string) => void;
  onAction: () => void;
  normalizedPhone: string | null;
  normalizing: boolean;
}

function SmartLinkBar({
  recipient,
  onRecipientChange,
  loading,
  acting = false,
  onSettingsClick,
  onQrClick,
  onAction,
  normalizedPhone,
  normalizing,
}: SmartLinkBarProps) {
  const recipientType = detectRecipientType(recipient);

  const buttonConfig = {
    empty: { label: "Create Trust Link", icon: "add_link" },
    phone: { label: "Send Trust Link", icon: "send" },
    email: { label: "Send Trust Link", icon: "send" },
    label: { label: "Create & Copy Link", icon: "add_link" },
  }[recipientType];

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") onAction();
    },
    [onAction],
  );

  const handleQr = useCallback(() => {
    onQrClick(recipient.trim() || undefined);
  }, [onQrClick, recipient]);

  const isDisabled = loading || acting;
  const iconBtn =
    "flex shrink-0 items-center justify-center rounded-lg border border-[#dac1c0] bg-white p-2.5 text-[#544242] transition-colors hover:bg-[#faf9ff] hover:text-[#161b27] disabled:opacity-50";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 rounded-xl border border-[#dac1c0] bg-[#faf9ff] p-2 sm:p-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-2 px-2">
          <Icon name="link" className="shrink-0 text-xl text-[#877271]" />
          <input
            type="text"
            value={recipient}
            onChange={(e) => onRecipientChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Optional: add a phone number, email, or label…"
            disabled={isDisabled}
            autoComplete="off"
            className="min-w-0 flex-1 border-0 bg-transparent py-1.5 text-base text-[#161b27] placeholder:italic placeholder:text-[#b9a9a8] outline-none focus:outline-none focus:ring-0 disabled:opacity-50 sm:text-lg"
            aria-label="Recipient phone, email, or label"
          />
          {recipient && !acting && (
            <button
              type="button"
              onClick={() => onRecipientChange("")}
              className="shrink-0 text-[#877271] transition-colors hover:text-[#161b27]"
              aria-label="Clear input"
            >
              <Icon name="close" className="text-base" />
            </button>
          )}
        </div>

        <button
          onClick={onAction}
          disabled={isDisabled}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-[#f08b8b] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#d97a7c] disabled:opacity-50 sm:px-5"
          aria-label={buttonConfig.label}
        >
          {acting ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            <>
              <Icon name={buttonConfig.icon} className="text-lg" />
              <span className="hidden sm:inline">{buttonConfig.label}</span>
            </>
          )}
        </button>

        <button onClick={handleQr} disabled={isDisabled} className={iconBtn} aria-label="Show QR code">
          <Icon name="qr_code" className="text-xl" />
        </button>
        <button onClick={onSettingsClick} disabled={loading} className={iconBtn} aria-label="Configure link requirements">
          <Icon name="settings" className="text-xl" />
        </button>
      </div>

      {/* Recipient type hints */}
      {recipientType === "phone" && (
        <div className="flex items-center justify-center gap-1.5">
          {normalizing ? (
            <>
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-[#dac1c0] border-t-[#974446]" />
              <span className="text-xs text-[#877271]">Checking number…</span>
            </>
          ) : (
            <>
              <Icon name="smartphone" className="text-sm text-[#974446]" />
              <span className="text-sm text-[#974446]">
                Send the Trust Link straight to{" "}
                <span className="font-semibold">
                  {normalizedPhone ? formatPhoneDisplay(normalizedPhone) : recipient.trim()}
                </span>
              </span>
            </>
          )}
        </div>
      )}
      {recipientType === "email" && (
        <div className="flex items-center justify-center gap-1.5">
          <Icon name="mail" className="text-sm text-[#974446]" />
          <span className="text-sm text-[#974446]">
            Send the Trust Link straight to <span className="font-semibold">{recipient.trim()}</span>
          </span>
        </div>
      )}
      {recipientType === "label" && (
        <div className="flex items-center justify-center gap-1.5">
          <Icon name="label" className="text-sm text-[#877271]" />
          <span className="text-sm text-[#544242]">
            Creating a link labelled{" "}
            <span className="font-semibold text-[#161b27]">&ldquo;{recipient.trim()}&rdquo;</span>
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AnimatedStepImage — step 2 APNG, replays periodically
// ---------------------------------------------------------------------------

function AnimatedStepImage() {
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    const initial = setTimeout(() => setAnimKey(1), 5000);
    return () => clearTimeout(initial);
  }, []);

  useEffect(() => {
    if (animKey === 0) return;
    const loop = setTimeout(() => setAnimKey((k) => k + 1), 20000);
    return () => clearTimeout(loop);
  }, [animKey]);

  return (
    <img
      key={animKey}
      src={animKey === 0 ? step2StaticImg : step2AnimImg}
      alt="Step 2 illustration"
      className="h-full w-auto object-contain"
    />
  );
}

// ---------------------------------------------------------------------------
// FeatureCardGrid — "How it works" cards
// ---------------------------------------------------------------------------

function FeatureCardGrid() {
  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-3">
        {FEATURE_CARDS.map((card) => (
          <div key={card.step} className={`flex flex-col p-5 ${CARD}`}>
            <div className="mb-4 flex h-28 items-center justify-center">
              {card.step === 2 ? (
                <AnimatedStepImage />
              ) : (
                <img src={card.image} alt={`Step ${card.step} illustration`} className="h-full w-auto object-contain" />
              )}
            </div>
            <span className="mb-1 text-[11px] font-bold uppercase tracking-widest text-[#877271]">
              Step {card.step}
            </span>
            <h3 className="text-sm font-semibold text-[#161b27]">{card.title}</h3>
            <p className="mt-1 text-xs leading-relaxed text-[#544242]">{card.description}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-[#fef0f0] px-4 py-3">
        <Icon name="shield" filled className="mt-0.5 shrink-0 text-lg text-[#974446]" />
        <p className="text-xs leading-relaxed text-[#544242]">
          <span className="font-semibold text-[#161b27]">Verified without exposure.</span>{" "}
          By default, neither party ever sees the other's documents — you simply receive a trusted
          confirmation that everything checks out.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CopyLinkButton
// ---------------------------------------------------------------------------

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition-colors ${
        copied ? "bg-[#e8f3ec] text-[#3f7d54]" : "bg-white text-[#544242] hover:text-[#974446]"
      }`}
      aria-label={copied ? "Copied" : "Copy link"}
    >
      <Icon name={copied ? "check" : "content_copy"} className="text-sm" />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ---------------------------------------------------------------------------
// ActivityRow
// ---------------------------------------------------------------------------

const STATUS_BG: Record<string, string> = {
  verified: "bg-[#e8f3ec]",
  pending: "bg-[#fff4e0]",
  denied: "bg-[#ffe6e3]",
  cancelled: "bg-[#f1f3ff]",
};

const STATUS_PILL: Record<string, string> = {
  verified: "bg-[#e8f3ec] text-[#3f7d54]",
  pending: "bg-[#fff4e0] text-[#92671e]",
  denied: "bg-[#ffe6e3] text-[#ba1a1a]",
  cancelled: "bg-[#f1f3ff] text-[#877271]",
};

function ActivityRow({
  conn,
  onOpenContact,
  userFirstName,
  anonIndex,
}: {
  conn: Connection;
  onOpenContact?: (id: number) => void;
  userFirstName?: string;
  anonIndex?: number;
}) {
  const sentViaSms = conn.sent_via === "sms";
  const sentViaEmail = conn.sent_via === "email";
  const isVerified = conn.status === "verified";

  const icon = sentViaSms ? "smartphone" : sentViaEmail ? "mail" : isVerified ? "check_circle" : "add_link";
  const iconBg = STATUS_BG[conn.status] ?? "bg-[#f1f3ff]";
  const iconColor = statusColorClass(conn.status);

  const firstName = userFirstName || "My";
  const anonLabel = `${firstName}'s Trust link ${String(anonIndex ?? 1).padStart(3, "0")}`;

  const labelText =
    (conn.label && !isPhoneLikeLabel(conn.label) ? conn.label : null) ||
    (conn.connected_customer
      ? `${conn.connected_customer.first_name} ${conn.connected_customer.last_name}`.trim()
      : null) ||
    (conn.label && isPhoneLikeLabel(conn.label) ? conn.label : null) ||
    anonLabel;

  const shortLink = conn.link ? conn.link.replace(/^https?:\/\//, "") : null;
  const handleRowClick = onOpenContact ? () => onOpenContact(conn.id) : undefined;

  return (
    <div
      className={`rounded-xl bg-white px-4 py-3 transition-shadow ${
        onOpenContact ? "cursor-pointer hover:shadow-[0px_8px_32px_rgba(26,31,43,0.08)]" : ""
      }`}
      onClick={handleRowClick}
      role={handleRowClick ? "button" : undefined}
      tabIndex={handleRowClick ? 0 : undefined}
      onKeyDown={
        handleRowClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleRowClick();
              }
            }
          : undefined
      }
    >
      <div className="flex items-center gap-3">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
          <Icon name={icon} filled={isVerified} className={`text-base ${iconColor}`} />
        </div>
        <span className="min-w-0 truncate text-sm font-medium text-[#161b27]">{labelText}</span>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <div className="flex flex-col items-end gap-1">
            <p className="text-xs text-[#877271]">{formatRelativeTime(conn.created)}</p>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATUS_PILL[conn.status] ?? "bg-[#f1f3ff] text-[#877271]"}`}>
              {statusLabel(conn.status)}
            </span>
          </div>
          {onOpenContact && <Icon name="chevron_right" className="shrink-0 text-base text-[#877271]" />}
        </div>
      </div>

      {shortLink && (
        <div
          className="mt-3 flex items-center gap-3 rounded-lg bg-[#faf9ff] px-4 py-2.5"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="flex-1 truncate text-center text-sm font-medium text-[#544242]">{shortLink}</span>
          <CopyLinkButton url={conn.link!} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RecentActivity card
// ---------------------------------------------------------------------------

interface RecentActivityProps {
  connections: Connection[];
  loading: boolean;
  onOpenContact: (id: number) => void;
  userFirstName: string;
}

function RecentActivity({ connections, loading, onOpenContact, userFirstName }: RecentActivityProps) {
  // Sequential index for anonymous links
  const anonIndexMap = new Map<number, number>();
  [...connections]
    .filter((c) => !c.label && !c.connected_customer)
    .sort((a, b) => a.created - b.created)
    .forEach((c, i) => anonIndexMap.set(c.id, i + 1));

  const rows = connections.slice(0, 3);

  return (
    <div className={`p-5 sm:p-6 ${CARD}`}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#161b27]">Recent Activity</h2>
        {connections.length > 0 && (
          <Link to="/activity" className="text-sm font-semibold text-[#974446] transition-colors hover:text-[#792e30]">
            View Full Ledger
          </Link>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-xl bg-[#f1f3ff]" />
          ))}
        </div>
      ) : connections.length === 0 ? (
        <div className="py-8 text-center">
          <Icon name="history" className="mx-auto mb-2 text-4xl text-[#877271]" />
          <p className="text-sm text-[#544242]">No recent activity yet. Share your trust link to get started.</p>
        </div>
      ) : (
        <div className="flex flex-col divide-y divide-[#f1f3ff]">
          {rows.map((conn) => (
            <ActivityRow
              key={conn.id}
              conn={conn}
              onOpenContact={onOpenContact}
              userFirstName={userFirstName}
              anonIndex={anonIndexMap.get(conn.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DashboardPage — Main Component
// ---------------------------------------------------------------------------

export default function DashboardPage() {
  const { user } = useAuth();

  // ---- State ----
  const [recentConnections, setRecentConnections] = useState<Connection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [globalLink, setGlobalLink] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [contactModalId, setContactModalId] = useState<number | null>(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [qrLink, setQrLink] = useState<string>("");
  const [qrRecipient, setQrRecipient] = useState<string | undefined>(undefined);
  const [linkConfig, setLinkConfig] = useState<CreateDirectLinkConfig | null>(null);
  const [savedLinkSettings, setSavedLinkSettings] = useState<TrustLinkSettingsV2 | null>(null);
  const [myAvatarUrl, setMyAvatarUrl] = useState<string | undefined>(undefined);
  const [myFactorData, setMyFactorData] = useState<MyFactorData>({});

  // ---- Smart bar state (controlled) ----
  const [barRecipient, setBarRecipient] = useState("");
  const [barActing, setBarActing] = useState(false);
  const [normalizedPhone, setNormalizedPhone] = useState<string | null>(null);
  const [normalizing, setNormalizing] = useState(false);
  const [globalCopied, setGlobalCopied] = useState(false);
  const [elevateDismissed, setElevateDismissed] = useState(false);

  // ---- Cache of freshly created Connection objects (link URLs survive refresh) ----
  const [createdLinks, setCreatedLinks] = useState<Connection[]>(() => {
    try {
      const stored = localStorage.getItem("uru_created_links");
      return stored ? (JSON.parse(stored) as Connection[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("uru_created_links", JSON.stringify(createdLinks.slice(0, 100)));
    } catch {
      // localStorage unavailable
    }
  }, [createdLinks]);

  // ---- Action feedback ----
  const [actionFeedback, setActionFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const [uruDisplayName, setUruDisplayName] = useState<string>("");
  const displayName =
    uruDisplayName || (user ? `${user.first_name} ${user.last_name}`.trim() : "") || user?.display_name || "User";
  const firstName = displayName.split(" ")[0] || "there";

  // Core KYC complete? (gov ID + liveness) — drives the "Elevate Trust Score" card
  const kyc = myFactorData.kyc ?? {};
  const coreVerified = !!(kyc.gov_id && kyc.liveness);

  // ---- Fetch profile + recent connections ----
  useEffect(() => {
    let cancelled = false;

    async function fetchProfile() {
      setProfileLoading(true);
      try {
        const uruMe = await getUruMe();
        if (cancelled) return;
        const name = `${uruMe.user.first_name} ${uruMe.user.last_name}`.trim();
        if (name) setUruDisplayName(name);

        const resolvedAvatar = resolveAvatarUrl(uruMe.user.avatar);
        if (resolvedAvatar) setMyAvatarUrl(resolvedAvatar);
        setMyFactorData({
          phone: uruMe.user.phone_number ? formatPhoneDisplay(uruMe.user.phone_number) : undefined,
          email: uruMe.user.email,
          kyc: {
            phone: uruMe.user.is_phone_verified || uruMe.kyc.is_phone_verified,
            email: uruMe.user.is_email_verified || uruMe.kyc.is_email_verified,
            gov_id: uruMe.kyc.is_gov_id_verified,
            liveness: uruMe.kyc.is_liveness_verified,
            ssn: uruMe.kyc.is_ssn_verified,
            address: uruMe.kyc.is_address_verified,
            ofac: uruMe.kyc.is_ofac_verified,
            signature: false,
            dl: false,
          },
        });

        const cfg = uruMe.connection_config ?? {};
        const config: CreateDirectLinkConfig = {
          require_gov_id: cfg["require_gov_id"] ?? true,
          require_liveness: cfg["require_liveness"] ?? true,
          require_ssn: cfg["require_ssn"] ?? false,
          require_ofac: cfg["require_ofac"] ?? false,
          require_phone: cfg["require_phone"] ?? true,
          require_email: cfg["require_email"] ?? true,
          require_address: cfg["require_address"] ?? true,
          require_signature: cfg["require_signature"] ?? false,
          require_dl_back: cfg["require_dl_back"] ?? false,
        };

        if (!cancelled && uruMe.global_link) setGlobalLink(uruMe.global_link);
        if (!cancelled) setLinkConfig(config);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiRequestError) {
          setError(err.message);
        } else if (err instanceof NetworkError) {
          setError("Unable to connect. Please check your internet connection.");
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    }

    async function fetchActivity() {
      try {
        const connections = await listConnections({ size: 5, sort: "-created" });
        if (cancelled) return;
        const enriched = await Promise.all(
          connections.map((conn) =>
            conn.source === "direct_link" && !conn.link ? getConnection(conn.id).catch(() => conn) : Promise.resolve(conn),
          ),
        );
        if (!cancelled) setRecentConnections(enriched);
      } catch {
        // Non-critical
      } finally {
        if (!cancelled) setConnectionsLoading(false);
      }
    }

    fetchProfile();
    fetchActivity();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Realtime refresh ----
  const fetchActivityRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    fetchActivityRef.current = async () => {
      try {
        const connections = await listConnections({ size: 5, sort: "-created" });
        const enriched = await Promise.all(
          connections.map((conn) =>
            conn.source === "direct_link" && !conn.link ? getConnection(conn.id).catch(() => conn) : Promise.resolve(conn),
          ),
        );
        setRecentConnections(enriched);
      } catch {
        // Non-critical
      }
    };
  });

  useEffect(() => {
    const refresh = () => {
      void fetchActivityRef.current?.();
    };
    const offAccepted = realtime.on("connection_accepted", refresh);
    const offKyc = realtime.on("kyc_completed", refresh);
    return () => {
      offAccepted();
      offKyc();
    };
  }, []);

  // ---- Phone normalization (debounced) ----
  useEffect(() => {
    setNormalizedPhone(null);
    const recipientType = detectRecipientType(barRecipient);
    if (recipientType !== "phone") return;

    setNormalizing(true);
    const t = setTimeout(async () => {
      try {
        const result = await normalizePhone({ phone_number: barRecipient.trim() });
        setNormalizedPhone(result.phone_number);
      } catch {
        // Normalization failed — raw input used as-is
      } finally {
        setNormalizing(false);
      }
    }, 500);

    return () => {
      clearTimeout(t);
      setNormalizing(false);
    };
  }, [barRecipient]);

  // ---- Auto-dismiss feedback ----
  useEffect(() => {
    if (!actionFeedback) return;
    const t = setTimeout(() => setActionFeedback(null), 3000);
    return () => clearTimeout(t);
  }, [actionFeedback]);

  // ---- Unified link action ----
  const handleBarAction = useCallback(async () => {
    if (barActing) return;
    const trimmed = barRecipient.trim();
    const recipientType = detectRecipientType(trimmed);
    const config = linkConfig ?? {};

    setBarActing(true);
    setActionFeedback(null);

    try {
      if (recipientType === "phone") {
        const phoneToSend = normalizedPhone ?? trimmed;
        const conn = await createDirectLink(config, phoneToSend);
        setCreatedLinks((prev) => [conn, ...prev.filter((c) => c.id !== conn.id)]);
        setActionFeedback({ type: "success", message: `Sent to ${formatPhoneDisplay(phoneToSend)}` });
      } else if (recipientType === "email") {
        const conn = await createDirectLink(config, trimmed);
        setCreatedLinks((prev) => [conn, ...prev.filter((c) => c.id !== conn.id)]);
        setActionFeedback({ type: "success", message: `Sent to ${trimmed}` });
      } else if (recipientType === "label") {
        const conn = await createDirectLink(config, trimmed);
        setCreatedLinks((prev) => [conn, ...prev.filter((c) => c.id !== conn.id)]);
        if (conn.link) await copyToClipboard(conn.link);
        setActionFeedback({ type: "success", message: `Link copied for "${trimmed}"` });
      } else {
        const conn = await createDirectLink(config);
        setCreatedLinks((prev) => [conn, ...prev.filter((c) => c.id !== conn.id)]);
        if (conn.link) await copyToClipboard(conn.link);
        setActionFeedback({ type: "success", message: "Trust link copied to clipboard" });
      }

      setBarRecipient("");
      void fetchActivityRef.current?.();
    } catch (err) {
      setActionFeedback({
        type: "error",
        message:
          err instanceof ApiRequestError
            ? err.message
            : err instanceof NetworkError
              ? "Unable to connect. Please check your internet connection."
              : "Failed to create link. Please try again.",
      });
    } finally {
      setBarActing(false);
    }
  }, [barActing, barRecipient, linkConfig, normalizedPhone]);

  // ---- Settings save ----
  const handleSettingsSave = useCallback(async (settings: TrustLinkSettingsV2) => {
    setSavedLinkSettings(settings);
    setError(null);

    const boolFlags = Object.fromEntries(settings.factors.map((f) => [`require_${f.key}`, f.theirMode !== "hide"]));

    const field_settings: Record<string, { share: string; ask: string }> = {};
    for (const f of settings.factors) {
      const share = f.myMode === "share" ? "share" : f.myMode === "valid" ? "show_valid" : "hide";
      const ask = f.theirMode !== "hide" ? "ask" : "dont_ask";
      field_settings[f.key] = { share, ask };
    }

    try {
      await saveGlobalLinkConfig({ ...boolFlags, field_settings });
      setLinkConfig(boolFlags);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else if (err instanceof NetworkError) {
        setError("Unable to connect. Please check your internet connection.");
      } else {
        setError("Failed to save link settings. Please try again.");
      }
    }
  }, []);

  const handleSettingsClick = useCallback(() => setShowSettingsModal(true), []);

  const handleQrClick = useCallback(
    (recipient?: string) => {
      setQrRecipient(recipient || undefined);
      setQrLink(globalLink ?? "");
      setShowQrModal(true);
    },
    [globalLink],
  );

  const handleQrBeforeShare = useCallback(async (): Promise<string> => {
    try {
      const conn = await createDirectLink(linkConfig ?? {}, qrRecipient);
      if (conn.link) {
        setCreatedLinks((prev) => [conn, ...prev.filter((c) => c.id !== conn.id)]);
        void fetchActivityRef.current?.();
        return conn.link;
      }
    } catch {
      // fall back to global link
    }
    return globalLink ?? "";
  }, [globalLink, linkConfig, qrRecipient]);

  // Merge cached link URLs into the activity feed
  const activityConnections = recentConnections.map((conn) => {
    const cached = createdLinks.find((c) => c.id === conn.id);
    return cached?.link && !conn.link ? { ...conn, link: cached.link } : conn;
  });

  // ---- Render ----
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 md:py-12">
      {/* Hero */}
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-[#161b27] sm:text-4xl">
          Welcome back, {firstName}.
        </h1>
        <p className="mt-1.5 text-base text-[#544242]">
          Your trust landscape is secure and active today.
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-[#ffe6e3] p-3 text-sm text-[#ba1a1a]" role="alert">
          <Icon name="error" className="mt-0.5 shrink-0 text-lg" />
          <p>{error}</p>
          <button onClick={() => setError(null)} className="ml-auto shrink-0 hover:opacity-70" aria-label="Dismiss error">
            <Icon name="close" className="text-lg" />
          </button>
        </div>
      )}

      {/* Global Trust Link card — link + copy + QR box */}
      <div className={`p-5 sm:p-6 ${CARD}`}>
        <div className="flex flex-col gap-5 md:flex-row md:items-stretch">
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fef0f0]">
                <Icon name="shield" filled className="text-xl text-[#974446]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#161b27]">Global Trust Link</h2>
                <p className="text-sm text-[#544242]">
                  Your permanent, encrypted gateway for sharing verified identity. Share this link to
                  establish instant secure connections.
                </p>
              </div>
            </div>
            <div className="flex items-stretch gap-2">
              <div className="flex min-w-0 flex-1 items-center rounded-lg border border-[#dac1c0] bg-[#faf9ff] px-3 py-2.5">
                <Icon name="link" className="mr-2 shrink-0 text-base text-[#877271]" />
                <span className="truncate font-mono text-sm text-[#544242]">
                  {globalLink ? globalLink.replace(/^https?:\/\//, "") : "Loading…"}
                </span>
              </div>
              <button
                onClick={() => {
                  if (!globalLink) return;
                  void copyToClipboard(globalLink).then((ok) => {
                    if (ok) {
                      setGlobalCopied(true);
                      setTimeout(() => setGlobalCopied(false), 2000);
                    }
                  });
                }}
                disabled={!globalLink}
                className={`flex shrink-0 items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-bold text-white transition-colors disabled:opacity-50 ${
                  globalCopied ? "bg-[#74a57f]" : "bg-[#f08b8b] hover:bg-[#d97a7c]"
                }`}
              >
                <Icon name={globalCopied ? "check" : "content_copy"} className="text-base" />
                {globalCopied ? "Copied" : "Copy Link"}
              </button>
            </div>
          </div>

          {/* QR box */}
          <button
            onClick={() => handleQrClick()}
            className="flex shrink-0 flex-col items-center justify-center gap-2 rounded-xl bg-[#e9edfe] p-5 transition-colors hover:bg-[#e4e8f9] md:w-44"
            aria-label="Show QR code"
          >
            <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-white">
              <Icon name="qr_code_2" className="text-5xl text-[#974446]" />
            </div>
            <span className="flex items-center gap-1 text-xs font-bold text-[#974446]">
              Open QR Code <Icon name="open_in_new" className="text-sm" />
            </span>
          </button>
        </div>
      </div>

      {/* Private Links + Trust Settings */}
      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        {/* Private Links */}
        <div className={`p-5 sm:p-6 lg:col-span-2 ${CARD}`}>
          <div className="mb-1 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#fef0f0]">
                <Icon name="add_link" className="text-lg text-[#974446]" />
              </div>
              <h2 className="text-lg font-bold text-[#161b27]">Private Links</h2>
            </div>
            {createdLinks.length > 0 && (
              <span className="rounded-full bg-[#e9edfe] px-2.5 py-1 text-[11px] font-bold text-[#2d5bbf]">
                {createdLinks.length} active
              </span>
            )}
          </div>
          <p className="mb-3 text-sm text-[#544242]">
            Single-use links for a specific person or session. Add a phone or email to send, or leave
            blank to copy.
          </p>

          <SmartLinkBar
            recipient={barRecipient}
            onRecipientChange={setBarRecipient}
            loading={profileLoading}
            acting={barActing}
            onSettingsClick={handleSettingsClick}
            onQrClick={handleQrClick}
            onAction={handleBarAction}
            normalizedPhone={normalizedPhone}
            normalizing={normalizing}
          />

          {actionFeedback && (
            <div
              className={`mt-3 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm ${
                actionFeedback.type === "success" ? "bg-[#e8f3ec] text-[#3f7d54]" : "bg-[#ffe6e3] text-[#ba1a1a]"
              }`}
              role="status"
              aria-live="polite"
            >
              <Icon name={actionFeedback.type === "success" ? "check_circle" : "error"} filled className="shrink-0 text-base" />
              <span className="font-medium">{actionFeedback.message}</span>
              <button onClick={() => setActionFeedback(null)} className="ml-auto shrink-0 opacity-60 hover:opacity-100" aria-label="Dismiss">
                <Icon name="close" className="text-base" />
              </button>
            </div>
          )}

          {createdLinks.filter((c) => c.link).length > 0 && (
            <div className="mt-3 divide-y divide-[#f1f3ff]">
              {createdLinks.filter((c) => c.link).slice(0, 2).map((c) => (
                <div key={c.id} className="flex items-center gap-2 py-2">
                  <Icon name="link" className="shrink-0 text-base text-[#877271]" />
                  <span className="min-w-0 flex-1 truncate font-mono text-xs text-[#544242]">
                    {c.link!.replace(/^https?:\/\//, "")}
                  </span>
                  <span className="shrink-0 text-[11px] text-[#877271]">{formatRelativeTime(c.created)}</span>
                  <CopyLinkButton url={c.link!} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Trust Settings — dark-rose accent card */}
        <button
          onClick={handleSettingsClick}
          className="flex flex-col items-start gap-3 rounded-2xl bg-[#974446] p-5 text-left text-white shadow-[0px_8px_32px_rgba(151,68,70,0.18)] transition-transform hover:-translate-y-0.5 sm:p-6"
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
            <Icon name="tune" className="text-2xl" />
          </div>
          <h2 className="text-lg font-bold">Trust Settings</h2>
          <p className="text-sm text-white/80">
            Fine-tune what you ask for and what you share on every handshake.
          </p>
          <span className="mt-auto rounded-lg bg-white px-4 py-2 text-sm font-bold text-[#974446]">Manage</span>
        </button>
      </div>

      {/* Recent Activity */}
      <div className="mt-5">
        <RecentActivity
          connections={activityConnections}
          loading={connectionsLoading}
          onOpenContact={setContactModalId}
          userFirstName={user?.first_name || "My"}
        />
      </div>

      {/* Elevate your Trust Score — until core KYC is complete */}
      {!profileLoading && !coreVerified && !elevateDismissed && (
        <div className={`mt-5 overflow-hidden sm:flex sm:items-stretch ${CARD}`}>
          <div className="h-28 bg-gradient-to-br from-[#f7a0a0] via-[#d97a7c] to-[#974446] sm:h-auto sm:w-44 sm:shrink-0" />
          <div className="flex flex-1 flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:p-6">
            <div className="min-w-0 flex-1">
              <span className="text-[11px] font-bold uppercase tracking-widest text-[#f08b8b]">Recommended</span>
              <h2 className="text-lg font-bold text-[#161b27]">Elevate your Trust Score</h2>
              <p className="text-sm text-[#544242]">
                Complete your identity verification (government ID + liveness) so others can trust you instantly.
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <Link
                to="/settings"
                className="rounded-lg bg-[#f08b8b] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#d97a7c]"
              >
                Start Verification
              </Link>
              <button
                onClick={() => setElevateDismissed(true)}
                className="rounded-lg border border-[#dac1c0] bg-white px-4 py-2.5 text-sm font-bold text-[#544242] transition-colors hover:bg-[#faf9ff]"
              >
                Remind me later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* How it works — collapsible */}
      <div className="mt-8">
        <button
          onClick={() => setShowHowItWorks((v) => !v)}
          className="flex items-center gap-1.5 text-lg font-bold text-[#161b27] transition-colors hover:text-[#974446]"
        >
          How it works
          <span
            className="material-symbols-outlined text-base transition-transform duration-200"
            style={{ transform: showHowItWorks ? "rotate(180deg)" : "rotate(0deg)" }}
          >
            expand_more
          </span>
        </button>
        {showHowItWorks && (
          <div className="mt-3">
            <FeatureCardGrid />
          </div>
        )}
      </div>

      {/* Modals */}
      {contactModalId !== null && (
        <ContactDetailModal connectionId={contactModalId} onClose={() => setContactModalId(null)} />
      )}
      <QrShareModal
        isOpen={showQrModal}
        onClose={() => setShowQrModal(false)}
        url={qrLink}
        title={displayName}
        onBeforeShare={handleQrBeforeShare}
      />
      <TrustLinkSettingsModalV2
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onSave={handleSettingsSave}
        initialSettings={savedLinkSettings ?? undefined}
        loading={false}
        myName={displayName}
        myAvatarUrl={myAvatarUrl}
        myData={myFactorData}
      />
    </div>
  );
}
